/**
 * Lifecycle enums and domain constants shared by the API and the web app (data-model §9).
 *
 * This is a leaf module with no intra-package imports, so `admin.ts` can import `LANGUAGES`
 * from here without creating an import cycle through the `index.ts` barrel (a cycle that native
 * ESM — the Vite dev server — evaluates into a "cannot access before initialization" TDZ error).
 */

export const TRIP_STATUSES = [
  "Enquiry",
  "ProposalSent",
  "Accepted",
  "Completed",
  "Cancelled",
  "Postponed",
  // The club let the proposal's acceptance deadline pass (TW UAT round 1). Not terminal — the club
  // can resubmit (Expired → Enquiry) to restart the proposal-building process.
  "Expired",
] as const;
export type TripStatus = (typeof TRIP_STATUSES)[number];

export const PROPOSAL_STATUSES = ["Draft", "Sent", "Accepted", "Expired"] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

// Invoice lifecycle (TW-3450). `Draft` is TC-internal and never visible to the club (a second RLS
// predicate hides it — data-model §10.3); `Issued`/`Paid` are club-visible.
export const INVOICE_STATUSES = ["Draft", "Issued", "Paid"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

// Document kinds (data-model §7). TC-uploaded attachments are `GENERAL`; generated PDFs carry their
// own kind. `MANIFEST` rows are produced by the passenger export (TW-3449).
export const DOCUMENT_KINDS = ["PROPOSAL_PDF", "INVOICE", "MANIFEST", "GENERAL"] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export const USER_TYPES = ["BACK_OFFICE", "CLUB_USER"] as const;
export type UserType = (typeof USER_TYPES)[number];

export const LANGUAGES = ["es", "en"] as const;
export type Language = (typeof LANGUAGES)[number];

/** Shape returned by the API health endpoints. */
export interface HealthStatus {
  status: "ok" | "error";
  info?: Record<string, { status: "up" | "down"; message?: string }>;
}
